import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { clipboardCopy } from '../../utils'

import { getUnmaskedDepositAccount } from '../../api/depositAccount'
import { getWithdrawalAccount } from '../../api/withdrawalAccount'

import { StoresContext } from '../../provider/StoreProvider'

import { useUserInfo } from '../../hooks/useUserInfo'
import { useCheckPrevPathname } from '../../hooks/useCheckPrevPathname'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { RowBetween } from '../../components/common'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { StyledText, Text } from '../../components/text'
import { Footer, FOOTER_MIN_HEIGHT } from '../../components/footer'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'

import { Color } from '../../theme/color'

export function SuccessDeposit() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)

  const { isLogin, isCheckingLogin } = useUserInfo()
  const isValidPrevPathname = useCheckPrevPathname('/account/deposit/terms')

  const unmaskedDepositAccount = useQuery({
    queryKey: [QUERY_KEY.depositAccount, QUERY_KEY.unmasked],
    queryFn: getUnmaskedDepositAccount,
  })
  const withdrawalAccount = useQuery({ queryKey: [QUERY_KEY.withdrawalAccount], queryFn: getWithdrawalAccount })

  const redirectHref = sessionStorage.getItem('createAccountSuccessCallbackLink')

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (isValidPrevPathname) {
      setInvalidAccess(true)
    }
  }, [isValidPrevPathname])

  useEffect(() => {
    sessionStorage.setItem('isLastAccountCreatePage', 'true')
    setShowTab(false)
    return () => {
      sessionStorage.removeItem('createAccountSuccessCallbackLink')
    }
  }, [])

  if (
    unmaskedDepositAccount.isPending ||
    unmaskedDepositAccount.isError ||
    withdrawalAccount.isPending ||
    withdrawalAccount.isError
  ) {
    return null
  }

  const copyAccountNumber = async () => {
    if (!unmaskedDepositAccount.data) return
    clipboardCopy(unmaskedDepositAccount.data.account, {
      message: '예치금 계좌 번호 복사가 완료되었어요.',
      isFooter: false,
    })
  }

  return (
    <Wrap>
      <StyledContent backgroundColor="gray_1">
        <AccountInfoContainer>
          <Text size={24} weight={600} lineHeight={34}>
            예치금 충전 계좌 발급이
            <br />
            완료되었어요.
          </Text>
          <SubTitle size={18} weight={600} block={true}>
            예치금 충전 계좌
          </SubTitle>
          <BankContainer>
            <RowBetween>
              <BankInfo>
                <BankIcon size={46} name="NH농협은행" />
                <Text size={18} weight={600}>
                  NH농협은행
                </Text>
              </BankInfo>
              <Button type="primary" size="compact" onClick={copyAccountNumber}>
                계좌번호 복사
              </Button>
            </RowBetween>
            <RowBetween>
              <Text size={14} weight={600} color="text_secondary">
                계좌번호
              </Text>
              <Text size={14} color="text_secondary">
                {unmaskedDepositAccount.data.account}
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={14} weight={600} color="text_secondary">
                예금주
              </Text>
              <Text size={14} color="text_secondary">
                {unmaskedDepositAccount.data.bankOwner}
              </Text>
            </RowBetween>
          </BankContainer>
        </AccountInfoContainer>
        <NoticeContainer
          title="안내사항"
          list={[
            <Text size={14} lineHeight={22} color="error">
              예치금 충전은 등록하신 출금 계좌로만 가능해요.
            </Text>,
            '토스/카카오뱅크 내 오픈 뱅킹 연동 계좌나 간편 결제(네이버페이, 카카오페이 등)를 통한 입금은 금융사 정책으로 인해 제한돼요.',
            '예치금 충전 시, 은행 정산 상태에 따라 최대 10분 정도 소요될 수 있어요.',
            '은행점검시간(23:45 ~ 00:15)에는 충전 및 출금 신청이 제한 될 수 있어요.',
          ]}
          $backgroundColor="gray_1"
        />
      </StyledContent>
      <Footer>
        <Button
          type="primary"
          size="large"
          block={true}
          href={redirectHref || '/myPage'}
          routeProps={{ replace: true }}
        >
          확인
        </Button>
      </Footer>
    </Wrap>
  )
}

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`
const AccountInfoContainer = styled.div`
  padding: 56px 24px 16px;
  background-color: ${Color.white};
`

const SubTitle = styled(StyledText)`
  margin: 32px 0 16px;
`

const BankContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  padding-bottom: 24px;
  > ${RowBetween} {
    margin-bottom: 8px;
  }
`

const BankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`
